import CustomButton from "../CustomButton";
import CustomInput from "../CustomInput";
import CustomSelect from "../CustomSelect";
import ModalLayout from "../layouts/ModalLayout";
import {
  CreateInvoicePayload,
  InvoiceStatus,
  PaymentCycle,
  InvoiceItemPayload,
  DiscountType,
} from "../../types/feesAndPaymentType";
import { useEffect, useState } from "react";
import SubButton from "../SubButtons";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  clearStudents,
  getStudents,
  setSearchFilters,
} from "../../features/student/StudentSlice";
import { fetchClasses } from "../../features/classes/ClassesSlice";
import feesAndPaymentApis from "../../api/feesAndPaymentApis";
import { toast } from "sonner";
import RecurringInvoice from "./RecurringInvoice";
import { fetchInvoices } from "../../features/feesAndPayment/FeesAndPaymentSlice";
import InvoiceItemsList from "../../pages/fees-and-payment/InvoiceItemList";
import { useBranchCode } from "../../util/hooks/useBranch";
import AcademicCycleSelect from "../common-input/AcademicCycle";

import InvoiceItemsTable from "../invoice/InvoiceItemsTable";
import InvoiceTotalsSection from "../invoice/InvoiceTotalsSection";
import { useCreateInvoice } from "../../util/hooks/useInvoice";

interface Props {
  onClose?: () => void;
}

export interface InvoiceRow {
  id: string;
  itemId?: number;
  itemName: string;
  amount: string;
  discount: string;
  discountType: string;
  discountRemark: string;
  gst: string;
}

interface RecurringSettings {
  paymentCycle: PaymentCycle;
  recurringFrom: string;
  recurringTo: string;
  occurrences: number;
}

export default function CreateInvoice({ onClose }: Props) {
  const dispatch = useDispatch<AppDispatch>();

  const { rows, totals, addRow, updateValue, setRows, calculateTotals } =
    useCreateInvoice();

  const { students, pagination, searchFilters } = useSelector(
    (state: RootState) => state.student,
  );
  const { classes, page, size, filters } = useSelector(
    (state: RootState) => state.classes,
  );

  // Form state
  const [isRecurring, setIsRecurring] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [paymentTitle, setPaymentTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selectedAcademicCycles, setSelectedAcademicCycles] = useState<
    { id: string; label: string }[]
  >([]);

  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [recurringSettings, setRecurringSettings] = useState<RecurringSettings>(
    {
      paymentCycle: "MONTHLY",
      recurringFrom: "",
      recurringTo: "",
      occurrences: 0,
    },
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const branchCode = useBranchCode();

  const { invoices } = useSelector((state: RootState) => state.fees);
  const schoolCode = useSelector(
    (state: RootState) => state.access.schoolBranch,
  );

  useEffect(() => {
    dispatch(
      fetchInvoices({
        branchCode: branchCode,
        schoolCode: schoolCode.schoolCode,
        page,
        size,
      }),
    );
  }, [page, size, branchCode, schoolCode, dispatch]);

  const filteredInvoices = invoices.filter(
    (inv) =>
      inv.paymentStatus === "SCHEDULED" || inv.paymentStatus === "PENDING",
  );

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchClasses({
        branchCode,
        page,
        size,
        filters: selectedAcademicCycles.length
          ? { academicCycle: selectedAcademicCycles[0].label }
          : {},
      }),
    );
  }, [dispatch, branchCode, page, size, selectedAcademicCycles]);

  useEffect(() => {
    dispatch(
      getStudents({
        branchCode,
        page: pagination.currentPage,
        size: pagination.pageSize,
        ...searchFilters,
      }),
    );
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    branchCode,
    searchFilters,
  ]);

  // Get student options
  const studentOptions = selectedClasses
    ? students.length > 0
      ? students.map((s) => ({
          label: s.fullName,
          value: s.admissionNumber?.toString() || "",
        }))
      : [
          {
            label: "No students in this class",
            value: "",
            disabled: true,
          },
        ]
    : [];

  // Get class options
  const classOptions = selectedAcademicCycles
    ? classes.length > 0
      ? classes?.map((c) => ({
          label: c.className,
          value: c.classId?.toString() || "",
        }))
      : [
          {
            label: "No classes",
            value: "",
            disabled: true,
          },
        ]
    : [];

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    switch (name) {
      case "paymentTitle":
        setPaymentTitle(value);
        break;
      case "dueDate":
        setDueDate(value);
        break;
      // case "academicCycle":
      //   // setSelectedAcademicCycle(value);
      //   setSelectedClasses([]);
      //   setSelectedStudents([]);
      //   break;
      // case "class":
      //   // setSelectedClass(value);
      //   setSelectedStudents([]);
      //   dispatch(clearStudents());
      //   dispatch(
      //     setSearchFilters({
      //       classId: value,
      //     }),
      //   );
      //   break;

      // case "student":
      //   // setSelectedStudent(value);
      //   break;
    }
  };

  const formatDateForAPI = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toISOString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!paymentTitle || !dueDate || rows.length === 0) {
      toast.error("Please fill all required fields and add at least one item");
      return;
    }

    if (selectedClasses.length === 0 || selectedStudents.length === 0) {
      toast.error("Please select a class and student");
      return;
    }

    if (
      isRecurring &&
      (!recurringSettings.recurringFrom || !recurringSettings.recurringTo)
    ) {
      toast.error("Please configure recurring settings");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const status: InvoiceStatus = isRecurring ? "SCHEDULED" : "PENDING";

      // Prepare invoice items
      const invoiceItems: InvoiceItemPayload[] = rows.map((row) => {
        const baseAmount = Number(row.amount || 0);
        const discount = Number(row.discount || 0);
        const gstPercentage = parseFloat(row.gst.replace("%", "") || "0");

        let finalAmount = baseAmount;
        let discountAmount = 0;

        if (row.discountType === "%" && discount > 0) {
          discountAmount = (baseAmount * discount) / 100;
          finalAmount = baseAmount - discountAmount;
        } else if (row.discountType === "₹" && discount > 0) {
          discountAmount = discount;
          finalAmount = baseAmount - discount;
        }

        const taxAmount = finalAmount * (gstPercentage / 100);
        const discountType: DiscountType =
          row.discountType === "%" ? "PERCENTAGE" : "FLAT";

        return {
          itemTitle: row.itemName,
          baseAmount: baseAmount,
          finalAmount: finalAmount + taxAmount,
          discount: discountAmount,
          discountType: discountType,
          discountRemarks: row.discountRemark || undefined,
          tax: gstPercentage,
          taxAmount: taxAmount,
        };
      });

      const selectedClassObj = classes.filter((c) =>
        selectedClasses.includes(c.classId?.toString() || ""),
      );

      const selectedStudentObj = students.filter((s) =>
        selectedStudents.includes(s.admissionNumber?.toString() || ""),
      );

      if (!selectedClassObj || !selectedStudentObj) {
        toast.error("Invalid selection. Please try again.");
        return;
      }

      const payload: CreateInvoicePayload = {
        paymentTitle,
        paymentCycle: isRecurring ? recurringSettings.paymentCycle : null,
        recurringFrom: isRecurring
          ? formatDateForAPI(recurringSettings.recurringFrom)
          : null,
        recurringTo: isRecurring
          ? formatDateForAPI(recurringSettings.recurringTo)
          : null,
        dueDate: formatDateForAPI(dueDate) || new Date().toISOString(),
        occurrences: isRecurring ? recurringSettings.occurrences : null,
        amount: totals.grandTotal,
        toClassIds: selectedClasses.map(Number),
        toAcademicCycleId: selectedAcademicCycles.map((cycle) =>
          Number(cycle.id),
        ),
        toStudentIds: selectedStudents.map(Number),
        invoiceItems,
        branchCode,
      };

      await feesAndPaymentApis.createInvoice(payload, status);
      await dispatch(
        fetchInvoices({
          branchCode,
          schoolCode: schoolCode.schoolCode,
          page,
          size,
        }),
      );

      toast.success("Invoice created successfully!");

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Create invoice failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecurringToggle = () => {
    const newValue = !isRecurring;
    setIsRecurring(newValue);
    if (newValue) {
      setShowRecurringModal(true);
    }
  };

  const handleSaveRecurringSettings = (settings: RecurringSettings) => {
    setRecurringSettings(settings);
    setShowRecurringModal(false);
  };

  const hasInvalidAmount = rows.some(
    (row) => !row.amount || Number(row.amount) <= 0,
  );

  return (
    <>
      <ModalLayout onClose={onClose} width="max-w-6xl">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <h4 className="text-xl font-semibold text-neutral-400">
              New Invoice
            </h4>
            <p className="text-warning-600 font-medium text-sm">
              Number of Student Selected : {selectedStudents.length || 0}
            </p>
          </div>
          <form
            className="flex flex-col gap-4 max-h-[340px] custom-scrollbar px-2 overflow-y-auto"
            onSubmit={handleSubmit}
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="flex flex-wrap gap-2">
                <AcademicCycleSelect
                  name="academicCycle"
                  value={selectedAcademicCycles[0]?.id || ""}
                  label="Academic Cycle"
                  placeholder="Select Academic Cycle"
                  required
                  onChange={(e) => {
                    const value = e.target.value;
                    const label = e.target.dataset?.label;

                    if (!value || !label) return;

                    setSelectedAcademicCycles([{ id: value, label }]);

                    // setSelectedAcademicCycles((prev) =>
                    //   prev.some((c) => c.id === value)
                    //     ? prev
                    //     : [...prev, { id: value, label }],
                    // );

                    setSelectedClasses([]);
                    setSelectedStudents([]);
                  }}
                />

                {/* {selectedAcademicCycles.map((cycle) => (
                  <span
                    key={cycle.id}
                    className="flex items-center gap-1 bg-primary-100 text-primary-700 px-2 py-1 rounded text-xs"
                  >
                    {cycle.label}
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedAcademicCycles((prev) =>
                          prev.filter((c) => c.id !== cycle.id),
                        )
                      }
                      className="ml-1 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))} */}
              </div>

              <div className="flex flex-wrap gap-2 mb-1">
                <CustomSelect
                  name="class"
                  value={selectedClasses[0] || ""}
                  // options={classOptions.filter(
                  //   (o) => !selectedClasses.includes(o.value.toString()),
                  // )}
                  options={classOptions}
                  label="Class"
                  placeholder="Select Class"
                  required
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;

                    // setSelectedClasses((prev) =>
                    //   prev.includes(value) ? prev : [...prev, value],
                    // );
                    setSelectedClasses([value]);

                    setSelectedStudents([]);
                    dispatch(clearStudents());
                    dispatch(setSearchFilters({ classId: value }));
                  }}
                />
                {/* {selectedClasses.map((cls) => (
                  <span
                    key={cls}
                    className="flex items-center gap-1 bg-primary-100 text-primary-700 px-2 py-1 rounded text-xs"
                  >
                    {classOptions.find((c) => c.value === cls)?.label}
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedClasses((prev) =>
                          prev.filter((id) => id !== cls),
                        )
                      }
                      className="ml-1 text-primary-700 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))} */}
              </div>

              <div className="flex flex-wrap gap-2">
                <CustomSelect
                  name="student"
                  value={selectedStudents[0] || ""}
                  label="Select Student"
                  placeholder="Select Student"
                  options={studentOptions}
                  required
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;

                    // setSelectedStudents((prev) =>
                    //   prev.includes(value) ? prev : [...prev, value],
                    // );
                    setSelectedStudents([value]);
                  }}
                />

                {/* {selectedStudents.map((studentId) => (
                  <span
                    key={studentId}
                    className="flex items-center gap-1 bg-primary-100 text-primary-700  px-2 py-1 rounded text-xs"
                  >
                    {studentOptions.find((s) => s.value === studentId)?.label}
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedStudents((prev) =>
                          prev.filter((id) => id !== studentId),
                        )
                      }
                      className="ml-1 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))} */}
              </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-3 w-full mt-2">
              {/* <CustomSelect
                name="template"
                value=""
                options={["Payment Pending"]}
                onChange={() => {}}
                placeholder="Select Template"
                label="Select Template"
              /> */}
              <CustomInput
                name="paymentTitle"
                value={paymentTitle}
                onChange={handleChange}
                placeholder="Enter Payment Title"
                label="Payment Title"
                required
              />
              <CustomSelect
                name="year"
                value=""
                options={["2026-27", "2027-28", "2028-29"]}
                onChange={handleChange}
                placeholder=""
                label="Select Year"
              />
              <CustomInput
                name="dueDate"
                value={dueDate}
                type="date"
                onChange={handleChange}
                label="Due Date"
                required
              />
            </div>

            {/* Recurring Invoice Section */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurringCheckbox"
                  checked={isRecurring}
                  onChange={handleRecurringToggle}
                  className="w-4 h-4"
                />
                <label
                  htmlFor="recurringCheckbox"
                  className="text-sm font-semibold text-neutral-400 text-nowrap cursor-pointer"
                >
                  Recurring Invoice
                </label>
              </div>
              {isRecurring && (
                <button
                  type="button"
                  onClick={() => setShowRecurringModal(true)}
                  className="ml-2 text-primary-500 text-sm font-medium hover:underline"
                >
                  Configure Recurring Settings
                </button>
              )}
            </div>

            <div>
              {rows.length > 0 && (
                <>
                  <InvoiceItemsTable
                    rows={rows}
                    updateValue={updateValue}
                    addRow={addRow}
                    setRows={setRows}
                    calculateTotals={calculateTotals}
                  />
                </>
              )}
              {filteredInvoices.map((invoice) =>
                invoice.invoiceItems?.length > 0 ? (
                  <div
                    key={invoice.invoiceId}
                    className="mb-3 border rounded p-1"
                  >
                    <InvoiceItemsList invoice={invoice} />
                  </div>
                ) : null,
              )}
            </div>
            <div className="flex flex-col md:flex-row sticky bottom-0 bg-white pt-2 justify-between md:items-center gap-4">
              {rows.length === 0 && (
                <div className="flex gap-2 items-center justify-end">
                  <label className="text-sm font-semibold text-neutral-400 text-nowrap">
                    To create invoice start
                  </label>
                  <SubButton
                    text="Add Items"
                    color="bg-success-600"
                    onClick={addRow}
                  />
                </div>
              )}

              {rows.length > 0 && <InvoiceTotalsSection totals={totals} />}

              <div className="flex gap-4 justify-end items-end">
                <CustomButton
                  text="Close"
                  color="bg-neutral-200"
                  onClick={onClose}
                  type="button"
                />
                <CustomButton
                  text={isSubmitting ? "Creating..." : "Create Invoice"}
                  color="bg-primary-500"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    rows.length === 0 ||
                    !paymentTitle ||
                    !dueDate ||
                    selectedClasses.length === 0 ||
                    selectedStudents.length === 0 ||
                    hasInvalidAmount
                  }
                />
              </div>
            </div>
          </form>
        </div>
      </ModalLayout>

      {showRecurringModal && (
        <RecurringInvoice
          onClose={() => setShowRecurringModal(false)}
          onSave={handleSaveRecurringSettings}
          initialSettings={recurringSettings}
        />
      )}
    </>
  );
}
